import React, { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import ChildrenBlock, { ChildrenBlockContext } from './ChildrenBlock.js';
import { BlockContext } from './BlockViewer.js';
import { shallowEqual } from './utils.js';
import mergeRefs from "react-merge-refs";
export const WrapperContext = createContext(undefined);
export const BlockWrapperInstance = ({ children }) => {
    const [state, dispatch] = useState(() => ({ component: 'div', props: {} }));
    const updateWrapper = useCallback((replacement) => {
        dispatch(replacement);
    }, [dispatch]);
    const blockContext = useContext(BlockContext);
    const childrenContext = useContext(ChildrenBlockContext);
    const Component = state.component ?? 'div';
    const { ref: stateRef, ...stateProps } = state.props ?? {};
    const props = { ...(stateProps ?? {}) };
    const lastState = useRef({});
    let { ref: gutenbergRef, ...gutenbergBlockProps } = blockContext?.gutenbergBlockProps ?? {};
    const finalProps = blockContext?.gutenbergBlockProps ?
        {
            ...gutenbergBlockProps,
            ...props,
            className: [gutenbergBlockProps.className, props?.className].filter(e => e).join(' '),
            style: { ...(gutenbergBlockProps.style ?? {}), ...(props.style ?? {}) }
        } :
        { ...props };
    let refsList = [gutenbergRef, stateRef, childrenContext?.forwardedChildrenRef].filter(r => r);
    const ref = refsList.length > 0 ? mergeRefs(refsList) : undefined;
    if (!shallowEqual(lastState.current, finalProps)) {
        lastState.current = finalProps;
    }
    const contextValue = useMemo(() => {
        return ({ state: { component: Component, props: { ...(lastState.current ?? {}), ref } }, dispatch: updateWrapper });
    }, [Component, ref, lastState.current, updateWrapper, gutenbergRef]);
    return React.createElement(WrapperContext.Provider, { value: contextValue }, React.createElement(contextValue.state.component, contextValue.state.props, children));
};
Object.defineProperty(BlockWrapperInstance, 'name', {
    value: 'Wrapper',
});
const BlockWrapperBase = ({ blockWrapperComponent, children, ...props }, ref) => {
    const wrapperContext = useContext(WrapperContext);
    const dispatch = wrapperContext?.dispatch;
    const { component: currentComponent, props: currentProps } = wrapperContext?.state ?? {};
    useEffect(() => {
        const { ref: _ignored, ...previousProps } = currentProps ?? {};
        if (!wrapperContext || !dispatch) {
            throw new Error("Invalid state");
        }
        if (currentComponent !== blockWrapperComponent || !shallowEqual(previousProps, props)) {
            dispatch({ component: blockWrapperComponent, props: { ...props, ref } });
        }
    }, [dispatch]);
    if (!wrapperContext) {
        throw new Error("Invalid state");
    }
    return React.createElement(Fragment, {}, children);
};
Object.defineProperty(BlockWrapperBase, 'name', {
    value: 'BlockWrapper',
});
let BlockWrapperComponent = React.forwardRef(BlockWrapperBase);
export const BlockWrapper = Object.assign(BlockWrapperComponent, {
    A: generateForTag('a'),
    Div: generateForTag('div'),
    P: generateForTag('p'),
    Button: generateForTag('button'),
    Form: generateForTag('form'),
    Span: generateForTag('span'),
    Ul: generateForTag('ul'),
    Li: generateForTag('li'),
    Input: generateForTag('input'),
    Select: generateForTag('select'),
    TextArea: generateForTag('textarea'),
    Header: generateForTag('header'),
    Footer: generateForTag('footer'),
    Article: generateForTag('article'),
    Without: generateForTag(React.Fragment, 'Fragment'),
    ChildrenBlocks: generateForTag(ChildrenBlock, 'ChildrenBlock'),
});
function generateForTag(tag, name = tag) {
    let type = BlockWrapperComponent;
    let component = (props, ref) => {
        const forwardedProps = useMemo(() => ({
            ...props,
            blockWrapperComponent: tag,
            ref,
        }), [ref, props]);
        return React.createElement(type, forwardedProps);
    };
    Object.defineProperty(component, 'name', {
        value: 'BlockWrapper.' + name,
    });
    return React.forwardRef(component);
}

//# sourceMappingURL=BlockWrapper.js.map
